import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type Theme = "dark" | "light" | "system";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = "skysend-theme";

function getInitialTheme(): Theme {
  try {
    localStorage.setItem(STORAGE_KEY, "dark");
  } catch {
    // Continue with dark theme if storage is unavailable.
  }
  return "dark";
}

function applyTheme(_theme: Theme) {
  document.documentElement.classList.add("dark");
  document.documentElement.classList.remove("light");
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", "#09090b");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    applyTheme("dark");
    try {
      localStorage.setItem(STORAGE_KEY, "dark");
    } catch {
      // Dark theme remains active for this session.
    }
  }, [theme]);

  const setTheme = (_newTheme: Theme) => {
    setThemeState("dark");
    applyTheme("dark");
    try {
      localStorage.setItem(STORAGE_KEY, "dark");
    } catch {
      // Ignore unavailable storage.
    }
  };

  return <ThemeContext.Provider value={{ theme: "dark", setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
}
